import { useRef, useState } from 'react'

import type { Activity, MergeStrategy, Operation } from '@fit-editor/fit-core'

import { OperationPreviewSummary } from '@/components/edit/operation-preview-summary'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import { useOperationPreview } from '@/hooks/use-operation-preview'
import { selectSelectedActivity, useActivityStore } from '@/store/activity-store'
import { currentActivityAsync } from '@/worker/fit-client'

export interface MergeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const STRATEGY_LABELS: Record<MergeStrategy['onOverlap'], string> = {
  reject: 'Reject the merge (no overlap allowed)',
  keepBase: 'Keep this activity’s records where they overlap',
  keepOther: 'Keep the other activity’s records where they overlap',
}

/**
 * Merges another already-loaded activity into the selected one
 * (`applyOperation`'s `merge` case -- `edit/operations.ts`). The `other`
 * operand has to be a real, current `Activity` snapshot (not just an id)
 * -- `other`'s history is resolved off the main thread via
 * `currentActivityAsync` whenever the picked id changes, mirroring how
 * `splitSelectedAtTimestamps` already resolves the *selected* activity's
 * current snapshot before calling the worker. Reuses
 * `useOperationPreview` + `OperationPreviewSummary` exactly like every
 * other edit dialog once that snapshot is in hand, since `merge` is a
 * regular member of the `Operation` union and already surfaces its
 * `OperationWarning`s (overlap/gap/developer-field-conflict detection)
 * through the same replay path.
 */
export function MergeDialog({ open, onOpenChange }: MergeDialogProps) {
  const loaded = useActivityStore(selectSelectedActivity)
  const allActivities = useActivityStore((s) => s.activities)
  const applyOperationToSelected = useActivityStore((s) => s.applyOperationToSelected)

  const candidates = allActivities.filter((a) => a.id !== loaded?.id)

  const [otherId, setOtherId] = useState<string | null>(null)
  const [onOverlap, setOnOverlap] = useState<MergeStrategy['onOverlap']>('reject')
  const [otherActivity, setOtherActivity] = useState<Activity | null>(null)
  const [resolving, setResolving] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  // Guards against a stale response from a since-abandoned pick
  // overwriting a newer one -- same "latest request wins" shape as
  // `useOperationPreview`'s own request-id refs.
  const requestIdRef = useRef(0)

  // Resolves the picked candidate's *current* (possibly already-edited)
  // activity snapshot off the main thread whenever the selection changes.
  const handlePickOther = (id: string) => {
    setOtherId(id)
    setOtherActivity(null)
    if (id === '') {
      setResolving(false)
      return
    }
    const candidate = candidates.find((a) => a.id === id)
    if (candidate == null) return
    const requestId = ++requestIdRef.current
    setResolving(true)
    void currentActivityAsync(candidate.history).then(({ activity }) => {
      if (requestIdRef.current !== requestId) return
      setOtherActivity(activity)
      setResolving(false)
    })
  }

  const operation: Operation | null =
    otherActivity != null ? { type: 'merge', other: otherActivity, strategy: { onOverlap } } : null

  const preview = useOperationPreview(open ? loaded?.history : undefined, operation)

  if (loaded == null) return null

  const handleConfirm = async () => {
    if (operation == null) return
    setSubmitting(true)
    try {
      // `null` means the apply failed (the store already surfaced a
      // toast) -- keep the dialog open rather than closing it as if the
      // merge had actually been applied.
      const warnings = await applyOperationToSelected(operation)
      if (warnings != null) onOpenChange(false)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby="merge-description">
        <DialogTitle>Merge activities</DialogTitle>
        <DialogDescription id="merge-description">
          Merges another already-loaded activity into &quot;{loaded.fileName}&quot;. The other
          activity is left untouched; only this one changes.
        </DialogDescription>

        <div className="flex flex-col gap-3">
          {candidates.length === 0 ? (
            <p className="text-muted-foreground text-xs">
              Load at least one more activity to merge with this one.
            </p>
          ) : (
            <div className="flex flex-col gap-1">
              <label htmlFor="merge-other" className="text-foreground text-xs font-medium">
                Merge with
              </label>
              <select
                id="merge-other"
                value={otherId ?? ''}
                onChange={(e) => handlePickOther(e.target.value)}
                className="border-input bg-background text-foreground focus-visible:ring-ring rounded-md border px-2 py-1 text-sm outline-none focus-visible:ring-2"
              >
                <option value="">Select an activity&hellip;</option>
                {candidates.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.fileName}
                  </option>
                ))}
              </select>
              {resolving && (
                <p role="status" className="text-muted-foreground text-xs">
                  Loading selected activity&hellip;
                </p>
              )}
            </div>
          )}

          {candidates.length > 0 && (
            <div
              role="radiogroup"
              aria-label="If timestamps overlap"
              className="flex flex-col gap-1"
            >
              <p className="text-foreground text-xs font-medium">If timestamps overlap</p>
              {(Object.keys(STRATEGY_LABELS) as MergeStrategy['onOverlap'][]).map((strategy) => (
                <label key={strategy} className="text-foreground flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="merge-overlap-strategy"
                    value={strategy}
                    checked={onOverlap === strategy}
                    onChange={() => setOnOverlap(strategy)}
                  />
                  {STRATEGY_LABELS[strategy]}
                </label>
              ))}
            </div>
          )}

          {operation != null && <OperationPreviewSummary state={preview} ready />}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={operation == null || submitting}
            onClick={() => void handleConfirm()}
          >
            Merge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
